/**
 * HomePage — console-style entry to the interview instrument.
 *
 * Sprint 11.6 redesign: slate-navy hero band on top with the cream
 * wordmark + Mono tagline; cream main band below with a two-column
 * layout — input console on the left, live session preview on the
 * right. Footer crumb strip with a link to the knowledge base.
 *
 * No marketing cards, no gradients. The only chromatic accents are
 * the Sienna `i`-seed in the wordmark, the Sienna focus ring on the
 * input, and the Sienna primary buttons. Mint is reserved for the
 * "session loaded" success pill.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, PlayCircle } from 'lucide-react';
import type { InterviewSession } from '@shared/types.ts';
import type { InterviewGuideTemplate } from '@shared/guide-template';
import { getSessionByInvitation } from '../services/api';
import {
  buildGuideFromSession,
  buildGuideSummary,
  getTemplateGuide,
} from '../lib/guideTemplate';
import { KaivaIcon, KaivaWordmark } from './brand';
import { EmptyHint, ErrorPill, Skeleton } from './feedback';

type SelectionStatus = 'idle' | 'loading' | 'demo' | 'loaded' | 'error';

export function HomePage() {
  const navigate = useNavigate();
  const [invitationId, setInvitationId] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionName, setSelectedSessionName] = useState<string | null>(null);
  const [selectedSessionDescription, setSelectedSessionDescription] = useState<
    string | null
  >(null);
  const [sessionGuide, setSessionGuide] = useState<InterviewGuideTemplate | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [status, setStatus] = useState<SelectionStatus>('idle');

  const templateGuide = useMemo(() => getTemplateGuide(), []);
  const templateGuideData = useMemo(
    () => buildGuideSummary(templateGuide),
    [templateGuide],
  );

  const activeGuide = sessionGuide ?? (status === 'demo' ? templateGuide : null);
  const activeGuideData = useMemo(
    () => (activeGuide ? buildGuideSummary(activeGuide) : null),
    [activeGuide],
  );

  const sectionOutline = useMemo(() => {
    if (!activeGuide) return [];
    return activeGuide.sections.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [activeGuide]);

  const resolveInvitation = (rawId: string) => {
    const trimmed = rawId.trim();

    setSelectionError(null);
    setSessionGuide(null);

    if (trimmed === '' || trimmed === 'demo') {
      setSelectedSessionId('demo');
      setSelectedSessionName(templateGuideData.summary.name);
      setSelectedSessionDescription(templateGuideData.summary.description);
      setStatus('demo');
      return;
    }

    setStatus('loading');
    setSelectedSessionId(null);
    setSelectedSessionName(null);
    setSelectedSessionDescription(null);

    void getSessionByInvitation(trimmed)
      .then((response) => {
        const session = response.session as InterviewSession;
        const resolvedName =
          session.sessionName || session.structureName || templateGuideData.summary.name;
        const structureData = session.structureData as Record<string, unknown> | undefined;
        const resolvedDescription =
          (typeof structureData?.description === 'string' && structureData.description) ||
          templateGuideData.summary.description;

        setSelectedSessionId(session.id);
        setSelectedSessionName(resolvedName);
        setSelectedSessionDescription(resolvedDescription);

        const guide = buildGuideFromSession(session);
        if (guide) setSessionGuide(guide);
        setStatus('loaded');
      })
      .catch((error: unknown) => {
        const rawMessage =
          error instanceof Error ? error.message : 'Unable to find this invitation.';
        const message = rawMessage.includes('(404)')
          ? 'No invitation found with this ID. Try the demo or check the link from your email.'
          : rawMessage;
        setSelectionError(message);
        setStatus('error');
      });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    resolveInvitation(invitationId);
  };

  const handleRunDemo = () => {
    setInvitationId('');
    resolveInvitation('demo');
  };

  const handleStart = () => {
    navigate(`/session/${selectedSessionId ?? 'demo'}`);
  };

  return (
    <main
      className="flex min-h-screen flex-col"
      style={{
        background: 'hsl(var(--background))',
        color: 'hsl(var(--foreground))',
      }}
    >
      {/* Hero band — Slate Navy */}
      <section
        className="px-6 py-10 sm:px-12 sm:py-14"
        style={{
          background: 'hsl(var(--slate-navy))',
          color: 'hsl(var(--cream))',
          borderBottom: '1px solid hsl(var(--border))',
        }}
      >
        <div className="mx-auto flex w-full flex-col gap-5" style={{ maxWidth: 1120 }}>
          <div className="flex items-center justify-between gap-3">
            <span
              className="kv-eyebrow"
              style={{
                letterSpacing: '0.1em',
                color: 'hsl(var(--cream) / 0.55)',
              }}
            >
              Kaiva · Interview Instrument
            </span>
            <span className="inline-flex items-center gap-2">
              <KaivaIcon size={20} />
              <span
                style={{
                  fontSize: 10.5,
                  letterSpacing: '0.04em',
                  color: 'hsl(var(--cream) / 0.55)',
                }}
              >
                v1.1
              </span>
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <KaivaWordmark size={56} tone="linen" />
            <p
              className="kv-eyebrow"
              style={{
                fontSize: 11.5,
                letterSpacing: '0.16em',
                color: 'hsl(var(--cream) / 0.7)',
                maxWidth: 640,
              }}
            >
              The interview instrument for human knowledge
            </p>
          </div>
        </div>
      </section>

      {/* Main band — Linen two-column with substrate dot-grid (default workspace tier) */}
      <section className="relative flex-1 px-6 py-10 sm:px-12 sm:py-14">
        <div className="kv-substrate kv-substrate--subtle" aria-hidden="true" />
        <div
          className="relative z-10 mx-auto grid w-full gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
          style={{ maxWidth: 1120 }}
        >
          {/* Left — Console input */}
          <div className="flex flex-col gap-6">
            <ConsoleInput
              invitationId={invitationId}
              setInvitationId={setInvitationId}
              onSubmit={handleSubmit}
              onRunDemo={handleRunDemo}
              status={status}
              selectedSessionId={selectedSessionId}
            />

            {selectionError && status === 'error' && (
              <ErrorPill
                message={selectionError}
                onRetry={() => resolveInvitation(invitationId)}
              />
            )}
          </div>

          {/* Right — Preview */}
          <div className="flex flex-col">
            <div
              className="mb-2 kv-eyebrow kv-eyebrow--accent"
              style={{
                letterSpacing: '0.1em',
              }}
            >
              Preview
            </div>

            {status === 'idle' ? (
              <div
                className="rounded-2xl px-6 py-12"
                style={{
                  background: 'hsl(var(--muted) / 0.35)',
                  border: '1px dashed hsl(var(--border))',
                }}
              >
                <EmptyHint
                  title="Pick a session to begin"
                  body="Paste an invitation ID on the left, or run the demo to see how Kaiva works."
                />
              </div>
            ) : status === 'loading' ? (
              <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
                <Skeleton variant="card" />
                <Skeleton variant="card" />
                <Skeleton variant="card" />
              </div>
            ) : activeGuideData ? (
              <SessionPreviewCard
                title={selectedSessionName ?? activeGuideData.summary.name}
                description={
                  selectedSessionDescription ?? activeGuideData.summary.description
                }
                sectionCount={activeGuideData.summary.sectionCount}
                estimatedMinutes={activeGuideData.summary.estimatedMinutes}
                sections={sectionOutline.map((s) => ({
                  id: s.section_id,
                  title: s.title,
                  estimatedMinutes: s.estimated_minutes ?? 0,
                  blockCount: s.block_ids?.length ?? 0,
                }))}
                onStart={handleStart}
              />
            ) : null}
          </div>
        </div>
      </section>

      {/* Footer crumb */}
      <footer
        className="px-6 py-4 sm:px-12"
        style={{ borderTop: '1px solid hsl(var(--border))' }}
      >
        <div
          className="mx-auto flex w-full flex-wrap items-center justify-between gap-3"
          style={{ maxWidth: 1120 }}
        >
          <span
            className="kv-eyebrow"
            style={{
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            Workspace · default
          </span>
          <Link
            to="/knowledge"
            className="inline-flex items-center gap-1.5 kv-eyebrow transition-colors hover:[color:hsl(var(--sienna-deep))]"
            style={{
              color: 'hsl(var(--muted-foreground))',
              textDecoration: 'none',
            }}
          >
            <BookOpen className="h-3 w-3" aria-hidden="true" />
            Knowledge base
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      </footer>
    </main>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function ConsoleInput({
  invitationId,
  setInvitationId,
  onSubmit,
  onRunDemo,
  status,
  selectedSessionId,
}: {
  invitationId: string;
  setInvitationId: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  onRunDemo: () => void;
  status: SelectionStatus;
  selectedSessionId: string | null;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div
        className="kv-eyebrow kv-eyebrow--accent"
        style={{
          letterSpacing: '0.1em',
        }}
      >
        Open a session
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-2.5">
        <label htmlFor="invitation-id" className="sr-only">
          Invitation ID
        </label>
        <input
          id="invitation-id"
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={invitationId}
          onChange={(e) => setInvitationId(e.target.value)}
          placeholder="Paste invitation ID…"
          className="kv-console-input w-full transition-colors"
          style={{
            height: 44,
            padding: '0 14px',
            borderRadius: 10,
            background: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
            fontSize: 14,
            letterSpacing: '-0.005em',
            outline: 'none',
          }}
        />

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={status === 'loading'}
            className="kv-persimmon-cta inline-flex items-center gap-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              height: 36,
              padding: '0 16px',
              borderRadius: 'var(--radius-sm)',
              background: 'hsl(var(--persimmon))',
              color: 'hsl(var(--slate-navy))',
              border: '1px solid hsl(var(--persimmon))',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {status === 'loading' ? 'Loading…' : 'Open session'}
            {status !== 'loading' && (
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>

          <button
            type="button"
            onClick={onRunDemo}
            disabled={status === 'loading'}
            className="inline-flex items-center gap-1.5 transition-colors hover:[background:hsl(var(--muted))] disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Or run the demo
          </button>
        </div>
      </form>

      {(status === 'demo' || status === 'loaded') && selectedSessionId && (
        <div className="mt-1 inline-flex w-fit items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full"
            style={{
              padding: '3px 9px',
              fontSize: 11,
              background: 'hsl(var(--teal) / 0.12)',
              border: '1px solid hsl(var(--teal) / 0.4)',
              color: 'hsl(var(--teal-dark))',
              fontWeight: 500,
            }}
          >
            <span
              aria-hidden="true"
              className="inline-block rounded-full"
              style={{
                width: 6,
                height: 6,
                background: 'hsl(var(--teal))',
              }}
            />
            {status === 'demo' ? 'Demo loaded' : 'Session loaded'}
          </span>
          <span
            style={{
              fontSize: 10.5,
              letterSpacing: '0.04em',
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            #{selectedSessionId.slice(0, 8)}…
          </span>
        </div>
      )}
    </div>
  );
}

function SessionPreviewCard({
  title,
  description,
  sectionCount,
  estimatedMinutes,
  sections,
  onStart,
}: {
  title: string;
  description: string | null;
  sectionCount: number;
  estimatedMinutes: number;
  sections: Array<{ id: string; title: string; estimatedMinutes: number; blockCount: number }>;
  onStart: () => void;
}) {
  return (
    <article
      className="overflow-hidden"
      style={{
        background: 'hsl(var(--paper))',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid hsl(var(--hairline-2))',
        /* v1.6: no shadows on cards. Lift comes from Paper-on-Linen contrast. */
      }}
    >
      {/* Header — tonal-surface shift instead of 1px divider */}
      <div
        className="px-6 pb-5 pt-6"
        style={{ background: 'hsl(var(--off-white-container-high))' }}
      >
        <h2
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: 'hsl(var(--foreground))',
            letterSpacing: '-0.015em',
            lineHeight: 1.25,
            margin: 0,
          }}
        >
          {title}
        </h2>
        {(sectionCount > 0 || estimatedMinutes > 0) && (
          <p
            className="mt-1.5"
            style={{
              fontSize: 11.5,
              color: 'hsl(var(--muted-foreground))',
              letterSpacing: '0.02em',
            }}
          >
            {[
              sectionCount > 0
                ? `${sectionCount} section${sectionCount === 1 ? '' : 's'}`
                : null,
              estimatedMinutes > 0 ? `${estimatedMinutes} min` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-5 px-6 py-5">
        {description && (
          <p
            style={{
              fontSize: 13.5,
              lineHeight: 1.55,
              letterSpacing: '-0.003em',
              color: 'hsl(var(--foreground))',
              margin: 0,
            }}
          >
            {description}
          </p>
        )}

        {sections.length > 0 && (
          <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
            {sections.map((section, i) => (
              <li
                key={section.id}
                className="flex items-center gap-2.5 rounded-[10px] px-3 py-2"
                style={{
                  background: 'hsl(var(--muted) / 0.35)',
                  border: '1px solid hsl(var(--border))',
                }}
              >
                <span
                  aria-hidden="true"
                  className="inline-grid flex-shrink-0 place-items-center tabular-nums"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: 'hsl(var(--sienna) / 0.10)',
                    color: 'hsl(var(--sienna-deep))',
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  className="min-w-0 flex-1 truncate"
                  style={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: 'hsl(var(--foreground))',
                  }}
                >
                  {section.title}
                </span>
                <span
                  className="flex-shrink-0 tabular-nums"
                  style={{
                    fontSize: 10.5,
                    letterSpacing: '0.04em',
                    color: 'hsl(var(--muted-foreground))',
                    textTransform: 'uppercase',
                  }}
                >
                  {section.estimatedMinutes > 0 ? `${section.estimatedMinutes} min` : ''}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Footer — tonal-surface shift instead of 1px divider */}
      <div
        className="flex items-center justify-end px-6 py-4"
        style={{ background: 'hsl(var(--off-white-container-high))' }}
      >
        <button
          type="button"
          onClick={onStart}
          className="kv-persimmon-cta inline-flex items-center gap-1.5 transition-colors"
          style={{
            height: 36,
            padding: '0 16px',
            borderRadius: 'var(--radius-sm)',
            background: 'hsl(var(--persimmon))',
            color: 'hsl(var(--slate-navy))',
            border: '1px solid hsl(var(--persimmon))',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Start interview
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

export default HomePage;
